import _ from 'lodash';
import { createContext, useContext, useEffect, useRef } from 'react';
import { PUSH_WORKBENCH, PUSH_WORKBOOK, PUSH_WORKSTEP, type PersistenceLevel } from '@/core/flux.service';
import { type StateSynchronizer } from '@/services/stateSynchronizer.service';
import type { ReactNode } from 'react';
import type { ResettableStoreActions, ResettableStoreInstance } from '@seeqdev/utils';

/**
 * Registration contract for a persisted Zustand store.
 */
type ZustandStoreEntry<TState extends object = Record<string, unknown>, TDehydrated = unknown> = {
  /**
   * Stable persisted store name used in dehydrated workstep/workbook/workbench state.
   */
  storeName: string;
  /**
   * Persistence bucket this store belongs to.
   * - `WORKBENCH`: persisted per user
   * - `WORKBOOK`: persisted per workbook
   * - `WORKSHEET`: persisted per worksheet/workstep
   */
  persistenceLevel: PersistenceLevel;
  /**
   * Controls whether store updates automatically trigger `sqStateSynchronizer.push(...)`.
   * - `push-always` (default): push according to `persistenceLevel`
   * - `push-ignore`: persist/rehydrate the store, but do not auto-push on each state change
   *
   * Use `push-ignore` for stores whose workstep semantics are handled explicitly elsewhere.
   */
  pushOption?: 'push-always' | 'push-ignore';
  /**
   * Zustand Seeq store instance to persist.
   * Must expose a public `actions.reset()` implementation so force rehydrate can reset it to its baseline.
   */
  store: ResettableStoreInstance<TState, ResettableStoreActions<TState>>;
  /**
   * Converts the live store state into the persisted shape written into dehydrated state.
   */
  dehydrate: (state: TState) => TDehydrated;
  /**
   * Escape hatch for exceptional cases where clobbering the store's state does not correctly
   * rehydrate the store.
   *
   * Avoid using this for new stores. Dehydration should only pick store state, not manipulate it.
   * This exists only for rare item stores that must rebuild state during rehydrate (e.g. their
   * dehydrated items omit color, itemType, and iconClass, which must be reconstructed).
   *
   * @deprecated Transitional for the Flux trend item-store migration; those stores should be
   * refactored after Zustandification to not require custom rehydration.
   * TODO CRAB-62419: Remove once item stores no longer rebuild state on rehydrate.
   */
  rehydrate?: (dehydratedState: TDehydrated) => void;
};

type PersistedSnapshots = Record<string, unknown>;

type RegistryEntry = {
  entry: ZustandStoreEntry;
  unsubscribe: () => void;
  lastDehydrated?: unknown;
};

type PersistenceContextValue = {
  registerStore: (entry: ZustandStoreEntry<any, any>) => void;
  unregisterStore: (storeName: string) => void;
  updateRehydratedSnapshot: (storeName: string, data: unknown) => void;
};

type PersistenceStateSynchronizer = Pick<StateSynchronizer, 'addZustandStore' | 'removeZustandStore' | 'push'>;

export const PERSISTED_STORE_ALLOW_LIST = new Set<string>([
  'sqAiAssistantStore',
  'sqImpactReportStore',
  'sqActionBoardStore',
  'sqAnnotationStore',
  'sqLayoutStore',
  'sqDigitalFilterStore',
  'sqFftStore',
  'sqAggregationBinStore',
  'sqDashboardStore',
  'sqDisplayToolStore',
  'sqToolTemplateStore',
  'sqScalingTemplateStore',
  'sqWorkbenchLayoutStore',
  'sqSearchStore',
  'sqVantageTrendStore',
  'sqOperationalizeStatisticsSummaryStore',
  'sqPdfExportStore',
  'sqExploreFolderModalStore',
  'sqScorecardStore',
  'sqExploreWorkbookModalStore',
  'onnxStore',
  'sqCursorStore',
  'sqTrendScalingColumnStore',
  'clusteringV2Store',
  'isolationForestV2Store',
  'partialLeastSquaresV2Store',
  'principalComponentAnalysisV2Store',
  'selfOrganizingMapsV2Store',
  'sqStatisticsSummaryStore',
  'sqThresholdMetricStore',
  'sqPredictionPanelStore',
  'sqSignalForecastStore',
  'sqStoredTableStore',
]);

const PUSH_OPTION_BY_LEVEL: Record<
  Exclude<PersistenceLevel, 'NONE'>,
  typeof PUSH_WORKBENCH | typeof PUSH_WORKBOOK | typeof PUSH_WORKSTEP
> = {
  WORKBENCH: PUSH_WORKBENCH,
  WORKBOOK: PUSH_WORKBOOK,
  WORKSHEET: PUSH_WORKSTEP,
};

const PersistenceContext = createContext<PersistenceContextValue | null>(null);

type PersistenceProviderProps = {
  legacyStores?: ZustandStoreEntry<any, any>[];
  stateSynchronizer: PersistenceStateSynchronizer;
  children?: ReactNode;
};

export const PersistenceProvider = ({ legacyStores = [], stateSynchronizer, children }: PersistenceProviderProps) => {
  const registryRef = useRef<Map<string, RegistryEntry>>(new Map());
  const snapshotBufferRef = useRef<PersistedSnapshots>({});

  const registerStore = (entry: ZustandStoreEntry<any, any>) => {
    const { storeName, persistenceLevel, pushOption = 'push-always' } = entry;
    if (persistenceLevel === 'NONE') {
      throw new Error(`Persisted store "${storeName}" cannot have persistenceLevel "NONE".`);
    }

    if (!PERSISTED_STORE_ALLOW_LIST.has(storeName)) {
      throw new Error(`Persisted store "${storeName}" is not in the allow-list.`);
    }
    if (registryRef.current.has(storeName)) {
      throw new Error(`Persisted store "${storeName}" is already registered.`);
    }

    stateSynchronizer.addZustandStore(storeName, persistenceLevel, entry.store, entry.dehydrate, entry.rehydrate);

    const unsubscribe = entry.store.subscribe(() => {
      const newState = entry.store.getState();
      const newDehydrated = entry.dehydrate(newState);
      const existing = registryRef.current.get(entry.storeName);
      if (existing && _.isEqual(existing.lastDehydrated, newDehydrated)) {
        return;
      }

      if (existing) {
        existing.lastDehydrated = newDehydrated;
      }
      if (pushOption === 'push-always') {
        const mode = PUSH_OPTION_BY_LEVEL[persistenceLevel];
        stateSynchronizer.push(mode);
      }
    });

    registryRef.current.set(entry.storeName, {
      entry,
      unsubscribe,
      lastDehydrated: entry.dehydrate(entry.store.getState()),
    });

    const stored = snapshotBufferRef.current[entry.storeName];
    if (stored !== undefined) {
      if (entry.rehydrate) {
        entry.rehydrate(stored);
      } else {
        entry.store.setState(stored, false);
      }
    }
  };

  const unregisterStore = (storeName: string) => {
    const existing = registryRef.current.get(storeName);
    if (!existing) return;
    existing.unsubscribe();
    stateSynchronizer.removeZustandStore(storeName);
    registryRef.current.delete(storeName);
  };

  const updateRehydratedSnapshot = (storeName: string, data: unknown) => {
    snapshotBufferRef.current[storeName] = data;
  };

  useEffect(() => {
    legacyStores.forEach(registerStore);
    return () => {
      legacyStores.forEach((entry) => unregisterStore(entry.storeName));
    };
  }, [legacyStores]);

  const value = {
    registerStore,
    unregisterStore,
    updateRehydratedSnapshot,
  };

  return <PersistenceContext.Provider value={value}>{children}</PersistenceContext.Provider>;
};

const usePersistenceContext = () => {
  const context = useContext(PersistenceContext);
  if (!context) {
    throw new Error('usePersistenceContext must be used within PersistenceProvider.');
  }
  return context;
};

export const useRegisterPersistedStore = () => {
  const context = usePersistenceContext();
  return <TState extends object, TDehydrated>(entry: ZustandStoreEntry<TState, TDehydrated>) => {
    context.registerStore(entry);
    return () => {
      context.unregisterStore(entry.storeName);
    };
  };
};

export const usePersistenceSnapshotBuffer = () => {
  const context = usePersistenceContext();
  return context.updateRehydratedSnapshot;
};

export type { PersistedSnapshots, ZustandStoreEntry };
